import { Injectable } from "@angular/core";
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from "@angular/router";
import { Observable, map, catchError, of, forkJoin } from "rxjs";
import { CalAngularService } from "@cvx/cal-angular";
import { GroupNames, GroupName, GroupUtils } from "../utils/group-names";
import { GroupMembershipCacheService } from "../services/group-membership-cache.service";

/**
 * Interface for route data configuration
 */
export interface GroupAccessData {
  requiredGroups?: GroupName[];
  requireAllGroups?: boolean; // If true, user must be in ALL groups; if false, user must be in ANY group
}

/**
 * Guard service that checks if the currently logged user has access to resources
 * based on their membership in specified Azure AD groups
 */
@Injectable({
  providedIn: "root",
})
export class GroupAccessGuard implements CanActivate {
  constructor(
    private router: Router,
    private calAngularService: CalAngularService,
    private groupMembershipCache: GroupMembershipCacheService
  ) {}

  /**
   * Determines if a route can be activated based on group membership
   * @param route - The activated route snapshot containing route data
   * @param state - The router state snapshot
   * @returns Observable<boolean> indicating if access is allowed
   */ canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    const routeData: GroupAccessData = route.data as GroupAccessData;
    const requiredGroups = routeData.requiredGroups;
    const requireAllGroups = routeData.requireAllGroups ?? false;

    // If no groups are specified, allow access
    if (!requiredGroups || requiredGroups.length === 0) {
      return true;
    }

    // For each GroupName constant, check if user is in any of its constituent groups
    const groupChecks = requiredGroups.map((groupName) =>
      this.checkGroupMembership(groupName)
    );

    // Combine all group checks
    return this.combineGroupChecks(groupChecks, requireAllGroups).pipe(
      map((hasAccess) => {
        if (!hasAccess) {
          this.router.navigate(["no-access"]);
          return false;
        }
        return true;
      }),
      catchError((error) => {
        console.error("Error during group access check:", error);
        this.router.navigate(["no-access"]);
        return of(false);
      })
    );
  }

  /**
   * Combines multiple group check observables based on the requirement type
   * @param groupChecks - Array of observables for group membership checks
   * @param requireAllGroups - Whether all groups are required or just any one
   * @returns Observable<boolean> indicating if access requirements are met
   */
  private combineGroupChecks(
    groupChecks: Observable<boolean>[],
    requireAllGroups: boolean
  ): Observable<boolean> {
    if (groupChecks.length === 0) {
      return of(false);
    }

    if (groupChecks.length === 1) {
      return groupChecks[0];
    }

    // For multiple groups, we need to wait for all checks to complete
    return new Observable<boolean>((observer) => {
      const results: boolean[] = [];
      let completed = 0;

      groupChecks.forEach((check, index) => {
        check.subscribe({
          next: (result) => {
            results[index] = result;
            completed++;

            // Once all checks are complete, determine final result
            if (completed === groupChecks.length) {
              const finalResult = requireAllGroups
                ? results.every((r) => r === true) // ALL groups required
                : results.some((r) => r === true); // ANY group sufficient

              observer.next(finalResult);
              observer.complete();
            }
          },
          error: (error) => {
            console.error(`Group check error at index ${index}:`, error);
            results[index] = false;
            completed++;

            if (completed === groupChecks.length) {
              const finalResult = requireAllGroups
                ? results.every((r) => r === true)
                : results.some((r) => r === true);

              observer.next(finalResult);
              observer.complete();
            }
          },
        });
      });
    });
  }
  /**
   * Utility method to check if user is in a specific group
   * Can be used by components for conditional UI rendering
   * @param group - The group to check membership for
   * @returns Observable<boolean> indicating membership status
   */
  public isUserInGroup(group: GroupName): Observable<boolean> {
    return this.groupMembershipCache.isUserInGroup(group).pipe(
      catchError((error) => {
        console.error(`Error checking group membership for ${group}:`, error);
        return of(false);
      })
    );
  }

  /**
   * Utility method to check if user is in any of the specified groups
   * @param groups - Array of groups to check
   * @returns Observable<boolean> indicating if user is in any group
   */
  public isUserInAnyGroup(groups: GroupName[]): Observable<boolean> {
    if (!groups || groups.length === 0) {
      return of(false);
    }

    const checks = groups.map((group) => this.isUserInGroup(group));
    return this.combineGroupChecks(checks, false);
  }

  /**
   * Utility method to check if user is in all of the specified groups
   * @param groups - Array of groups to check
   * @returns Observable<boolean> indicating if user is in all groups
   */
  public isUserInAllGroups(groups: GroupName[]): Observable<boolean> {
    if (!groups || groups.length === 0) {
      return of(false);
    }

    const checks = groups.map((group) => this.isUserInGroup(group));
    return this.combineGroupChecks(checks, true);
  }

  /**
   * Checks if user is in any of the groups for a specific GroupName constant
   * Handles comma-separated group names similar to API behavior
   * @param groupName - The GroupName constant (may contain comma-separated groups)
   * @returns Observable<boolean> indicating if user is in any of the groups
   */
  private checkGroupMembership(groupName: GroupName): Observable<boolean> {
    const individualGroups = GroupUtils.getIndividualGroups(groupName);

    // If only one group, use the existing cache logic
    if (individualGroups.length === 1) {
      return this.groupMembershipCache
        .isUserInGroup(individualGroups[0] as GroupName)
        .pipe(
          catchError((error) => {
            console.error(
              `Error checking group membership for ${individualGroups[0]}:`,
              error
            );
            return of(false);
          })
        );
    }

    // For multiple groups, check if user is in ANY of them
    const groupChecks = individualGroups.map((group) =>
      this.calAngularService.isInGroup(group).pipe(
        catchError((error) => {
          console.error(`Error checking group membership for ${group}:`, error);
          return of(false);
        })
      )
    );

    return forkJoin(groupChecks).pipe(
      map((results: boolean[]) => results.some((result) => result === true)),
      catchError((error) => {
        console.error(
          `Error checking group membership for ${groupName}:`,
          error
        );
        return of(false);
      })
    );
  }
}
